import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { isPaid } from "@/lib/planActions";
import { currentSubscription } from "@/lib/push";

export const tomorrowIso = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };
export const notifState = () => (typeof Notification === "undefined" ? "unsupported" : Notification.permission);

export function useTomorrow(enabled) {
  return useQuery({ queryKey: ["tomorrow", tomorrowIso()], queryFn: async () => (await api.get("/reminders/tomorrow", { params: { date: tomorrowIso() } })).data, enabled });
}

// Fires one browser notification per day at the configured time while the app is open (Plus/Premium, opt-in)
export function useCookingReminder() {
  const { user } = useAuth();
  const { t, tv } = useLang();
  const rem = user?.settings?.reminders;
  const active = isPaid(user) && !!rem?.enabled && notifState() === "granted";
  const firing = useRef(false);

  useEffect(() => {
    if (!active) return undefined;
    const check = async () => {
      const now = new Date();
      const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      const today = now.toISOString().slice(0, 10);
      if (hhmm < (rem.time || "19:00") || localStorage.getItem("chenia_reminder_fired") === today || firing.current) return;
      firing.current = true;
      try {
        if (await currentSubscription()) { localStorage.setItem("chenia_reminder_fired", today); return; } // push handles it → no duplicate
        const { data } = await api.get("/reminders/tomorrow", { params: { date: tomorrowIso() } });
        const lines = data.meals.map((m) => `${t(m.slot)} : ${tv(m.name)}`);
        const tips = [...new Set(data.meals.flatMap((m) => m.tips))].map((c) => `• ${t(`tip_${c}`)}`);
        new Notification(t("reminder_push_title"), { body: [...lines, ...tips].join("\n") || t("no_tomorrow"), icon: "/favicon.ico" });
        localStorage.setItem("chenia_reminder_fired", today);
      } catch (error) { console.error("Cooking reminder failed:", error); }
      finally { firing.current = false; }
    };
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, [active, rem?.time, t, tv]);
}

// Rappel doux du journal (tous niveaux, opt-in, désactivé par défaut) : uniquement si rien n'a été noté aujourd'hui.
export function useJournalReminder() {
  const { user } = useAuth();
  const { t } = useLang();
  const rem = user?.settings?.journal_reminder;
  const active = !!rem?.enabled && notifState() === "granted";
  const firing = useRef(false);

  useEffect(() => {
    if (!active) return undefined;
    const check = async () => {
      const now = new Date();
      const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      if (hhmm < (rem.time || "20:00") || localStorage.getItem("chenia_journal_reminder_fired") === today || firing.current) return;
      firing.current = true;
      try {
        localStorage.setItem("chenia_journal_reminder_fired", today);
        if (await currentSubscription()) return; // push s'en charge → pas de doublon
        const { data } = await api.get("/food/day", { params: { date: today, member_id: "primary" } });
        if (data.count === 0) new Notification(t("j_rem_push_title"), { body: t("j_rem_body"), icon: "/favicon.ico", tag: "chenia-journal" });
      } catch (error) { console.error("Journal reminder failed:", error); }
      finally { firing.current = false; }
    };
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, [active, rem?.time, t]);
}
